'use strict';

import angular from 'angular';

/**
 * Service d'authentification
 */
export default angular.module('observatoireApp')
  .factory('Auth', ['$http', '$q', '$state', '$rootScope', 'StorageService', function($http, $q, $state, $rootScope, StorageService) {
    
    var AUTH_TOKEN_KEY = 'authToken';
    var CURRENT_USER_KEY = 'currentUser';
    
    // Pas d'utilisateur connecté par défaut
    var currentUser = null;
    
    // Récupération des données d'authentification persistantes
    // Vérifier s'il existe un utilisateur déjà stocké
    console.log('Vérification des données d\'authentification persistantes');
    try {
      var storedUser = StorageService.get(CURRENT_USER_KEY);
      if (storedUser) {
        currentUser = angular.fromJson(storedUser);
        console.log('Session utilisateur restaurée:', currentUser.username);
      }
    } catch (e) {
      console.error('Erreur lors de la restauration de session:', e);
      // Nettoyer uniquement en cas d'erreur
      StorageService.remove(CURRENT_USER_KEY);
      StorageService.remove(AUTH_TOKEN_KEY);
    }
    
    var service = {
      login: login,
      logout: logout,
      register: register,
      identity: identity,
      isAuthenticated: isAuthenticated,
      hasAuthority: hasAuthority,
      hasAnyAuthority: hasAnyAuthority,
      getToken: getToken,
      updateUser: updateUser,
      setCurrentUser: setCurrentUser
    };
    
    return service;
    
    /**
     * Définir l'utilisateur courant (pratique pour la simulation)
     * @param {Object} user - Données de l'utilisateur à définir
     */
    function setCurrentUser(user) {
      if (user) {
        currentUser = user;
        // S'assurer que l'utilisateur a un rôle par défaut
        if (!user.roles) {
          user.roles = ['ROLE_USER'];
        }
        // Stocker l'utilisateur dans le localStorage
        StorageService.set(CURRENT_USER_KEY, angular.toJson(user));
        // Générer un token simultané
        StorageService.set(AUTH_TOKEN_KEY, 'simulated-jwt-token-' + Date.now());
        
        // Envoyer un événement de connexion réussie
        $rootScope.$emit('auth:login-success', {user: user});
      }
      return user;
    }
    
    /**
     * Connexion utilisateur
     * @param {Object} credentials - Identifiants de connexion
     * @returns {Promise} - Promesse contenant l'utilisateur connecté
     */
    function login(credentials) {
      console.log('Auth.login appelé avec:', credentials.username);
      // Utiliser l'URL complète pour éviter les problèmes de proxy, comme pour l'inscription
      // Configurer la transformation de réponse pour gérer les réponses non-JSON
      return $http.post('/api/auth/login', credentials, {
        transformResponse: function(data) {
          // Essayer de parser comme JSON, sinon créer un objet avec la chaîne
          try {
            return angular.fromJson(data);
          } catch(e) {
            console.log('Réponse de connexion non-JSON:', data);
            // Si la réponse n'est pas un JSON valide, créer un objet simulant une réponse de connexion réussie
            // Le flag simulated permet d'identifier un utilisateur simulé
            return {
              success: true,
              message: data,
              simulated: true, // Marqueur pour les utilisateurs simulés
              // Générer un token simulé pour les tests
              token: 'simulated-jwt-token-' + Date.now(),
              id: 1,
              username: credentials.username,
              email: credentials.username + '@example.com',
              firstName: 'Prénom', // Ajout du prénom
              lastName: 'Nom',     // Ajout du nom
              roles: ['ROLE_USER'],
              preferredLanguage: 'fr'
            };
          }
        }
      })
      .then(function(response) {
        console.log('Réponse login reçue:', response.data);
        var data = response.data;
        
        // Si la connexion réussit, récupérer le profil complet de l'utilisateur depuis le backend
        if (data.success && data.id && !data.simulated) {
          return $http.get('/api/users/profile').then(function(profileResponse) {
            console.log('Profil utilisateur récupéré:', profileResponse.data);
            // Fusionner les données du profil avec les données de connexion
            angular.extend(data, profileResponse.data);
            return response;
          }).catch(function(error) {
            console.error('Erreur lors de la récupération du profil utilisateur:', error);
            // Continuer malgré l'erreur pour ne pas bloquer la connexion
            return response;
          });
        }
        return response;
      })
      .then(function(response) {
        var data = response.data;
        
        // Vérifier si la réponse contient un token
        if (!data.token) {
          console.error('Aucun token reçu dans la réponse');
          return $q.reject('Authentification échouée: pas de token');
        }
        
        StorageService.set(AUTH_TOKEN_KEY, data.token);
        
        var user = {
          id: data.id || 1,
          username: data.username || credentials.username,
          email: data.email || (credentials.username + '@example.com'),
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          roles: data.roles || ['ROLE_USER'],
          preferredLanguage: data.preferredLanguage || 'fr'
        };
        
        console.log('Utilisateur authentifié:', user);
        currentUser = user;
        StorageService.set(CURRENT_USER_KEY, angular.toJson(user));
        
        // Emettre un événement de login réussi
        $rootScope.$emit('auth:login-success', {user: user});
        
        return user;
      });
    }
    
    /**
     * Déconnexion utilisateur
     */
    function logout() {
      StorageService.remove(AUTH_TOKEN_KEY);
      StorageService.remove(CURRENT_USER_KEY);
      currentUser = null;
      $state.go('login');
    }
    
    /**
     * Inscription utilisateur
     * @param {Object} user - Données d'inscription
     * @returns {Promise} - Promesse contenant la réponse du serveur
     */
    function register(user) {
      console.log('Auth.register appelé avec:', user);
      // Debug détaillé des données envoyées
      console.log('Structure JSON envoyée au serveur:', JSON.stringify(user, null, 2));
      console.log('Champs présents:', Object.keys(user).join(', '));
      
      // Utiliser l'URL complète pour éviter les problèmes de proxy
      var url = '/api/auth/signup';
      console.log('URL appelée:', url);
      
      return $http.post(url, user)
        .then(function(response) {
          console.log('Réponse du serveur pour inscription:', response.data);
          console.log('Statut HTTP:', response.status);
          console.log('Headers:', response.headers());
          return response;
        })
        .catch(function(error) {
          console.error('Erreur lors de l\'appel à auth/signup:', error);
          if (error.data) console.error('Détails erreur:', error.data);
          console.error('Statut HTTP:', error.status);
          throw error;
        });
    }
    
    /**
     * Obtient l'identité de l'utilisateur courant
     * @returns {Promise} - Promesse contenant l'utilisateur courant
     */
    function identity() {
      var deferred = $q.defer();
      
      if (currentUser) {
        deferred.resolve(currentUser);
      } else {
        // Essayer de récupérer l'utilisateur depuis le stockage local
        var storedUser = StorageService.get(CURRENT_USER_KEY);
        var token = StorageService.get(AUTH_TOKEN_KEY);
        
        if (storedUser && token) {
          try {
            var user = angular.fromJson(storedUser);
            // Si c'est un utilisateur simulé par notre frontend, le supprimer
            if (token.startsWith('simulated-jwt-token-') || (user && user.simulated)) {
              console.log('Nettoyage d\'un utilisateur simulé au chargement de la page');
              StorageService.remove(AUTH_TOKEN_KEY);
              StorageService.remove(CURRENT_USER_KEY);
              deferred.resolve(null);
            } else {
              // Utiliser un utilisateur provenant d'une authentification réelle
              currentUser = user;
              deferred.resolve(user);
            }
          } catch (e) {
            console.error('Erreur lors du parsing de l\'utilisateur stocké:', e);
            StorageService.remove(AUTH_TOKEN_KEY);
            StorageService.remove(CURRENT_USER_KEY);
            deferred.resolve(null);
          }
        } else {
          deferred.resolve(null);
        }
      }
      
      return deferred.promise;
    }
    
    /**
     * Vérifie si l'utilisateur est authentifié
     * @returns {boolean} - Vrai si l'utilisateur est authentifié
     */
    function isAuthenticated() {
      return !!currentUser;
    }
    
    /**
     * Vérifie si l'utilisateur a une autorité spécifique
     * @param {string} authority - Autorité à vérifier
     * @returns {boolean} - Vrai si l'utilisateur a l'autorité
     */
    function hasAuthority(authority) {
      if (!currentUser) return false;
      var roles = currentUser.roles || currentUser.authorities || [];
      return roles.some(function(r) {
        return (typeof r === 'string') ? r === authority : (r.name === authority || r.authority === authority);
      });
    }
    
    /**
     * Vérifie si l'utilisateur a l'une des autorités spécifiées
     * @param {Array} authorities - Liste d'autorités à vérifier
     * @returns {boolean} - Vrai si l'utilisateur a au moins une des autorités
     */
    function hasAnyAuthority(authorities) {
      if (!currentUser) return false;
      return authorities.some(function(a) { return hasAuthority(a); });
    }
    
    /**
     * Obtient le token d'authentification
     * @returns {string} - Token d'authentification
     */
    function getToken() {
      return StorageService.get(AUTH_TOKEN_KEY);
    }
    
    /**
     * Met à jour les informations de l'utilisateur courant
     * @param {Object} user - Nouvelles informations utilisateur
     */
    function updateUser(user) {
      if (currentUser) {
        angular.extend(currentUser, user);
        StorageService.set(CURRENT_USER_KEY, angular.toJson(currentUser));
      }
    }
}]);
